import { useCallback, useEffect, useRef, useState } from 'react';
import { missingTimesheetActions } from '@core/timesheetApproval/missingTimesheet.action';
import type { MissingTimesheet } from '@core/timesheetApproval/missingTimesheet.types';
import { useTenantId } from '@/contexts/TenantContext';

type UseMissingTimesheets = {
  items: MissingTimesheet[];
  /** True until the first load has finished. */
  isLoading: boolean;
  /** True while already-loaded timesheets are refreshed in the background. */
  isRefreshing: boolean;
  error: string | null;
  refetch: () => Promise<void>;
};

/**
 * Loads scheduled shifts the candidate has not submitted between two
 * `YYYY-MM-DD` dates. Only call this for tenant staff who can see the
 * Awaiting submission tab.
 *
 * Loaded once per visit to the page. Switching away and back keeps the list
 * and does not fetch again. A page reload, a changed date range, or an
 * explicit refetch loads a fresh copy.
 */
export const useMissingTimesheets = (
  enabled: boolean,
  startDate: string,
  endDate: string,
): UseMissingTimesheets => {
  const tenantId = useTenantId();
  const canFetch = enabled && !!tenantId;

  const [items, setItems] = useState<MissingTimesheet[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadSessionRef = useRef(0);

  const load = useCallback(async () => {
    if (!canFetch || !tenantId) return;

    loadSessionRef.current += 1;
    const loadSession = loadSessionRef.current;

    setIsFetching(true);
    setError(null);
    try {
      const nextItems = await missingTimesheetActions.getMissingTimesheets(
        tenantId,
        startDate,
        endDate,
      );
      if (loadSession !== loadSessionRef.current) return;
      setItems(nextItems);
      setHasLoaded(true);
    } catch (fetchError) {
      if (loadSession !== loadSessionRef.current) return;
      console.error('Error fetching missing timesheets:', fetchError);
      setError('Unable to load timesheets awaiting submission');
    } finally {
      if (loadSession === loadSessionRef.current) {
        setIsFetching(false);
      }
    }
  }, [canFetch, tenantId, startDate, endDate]);

  useEffect(() => {
    if (!canFetch || hasLoaded) return;
    void load();
  }, [canFetch, hasLoaded, load]);

  const rangeKey = `${tenantId ?? ''}:${startDate}:${endDate}`;
  const rangeKeyRef = useRef(rangeKey);
  useEffect(() => {
    if (rangeKeyRef.current === rangeKey) return;
    rangeKeyRef.current = rangeKey;
    loadSessionRef.current += 1;
    setItems([]);
    setHasLoaded(false);
    setIsFetching(false);
  }, [rangeKey]);

  return {
    items,
    isLoading: isFetching && !hasLoaded,
    isRefreshing: isFetching && hasLoaded,
    error,
    refetch: load,
  };
};
